export type DocumentKind = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'text' | 'image' | 'other';

export const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.txt',
  '.csv',
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
] as const;

export const DOCUMENT_ACCEPT = DOCUMENT_EXTENSIONS.join(',');

export const ALLOWED_DOCUMENT_MIMES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

const EXT_TO_KIND: Record<string, DocumentKind> = {
  pdf: 'pdf',
  doc: 'word',
  docx: 'word',
  xls: 'excel',
  xlsx: 'excel',
  ppt: 'powerpoint',
  pptx: 'powerpoint',
  txt: 'text',
  csv: 'text',
  jpg: 'image',
  jpeg: 'image',
  png: 'image',
  gif: 'image',
  webp: 'image',
};

export function getFileExtension(filename: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot === -1) return '';
  return filename.slice(dot + 1).toLowerCase();
}

export function getDocumentKind(filename: string, mime?: string): DocumentKind {
  const ext = getFileExtension(filename);
  if (ext && EXT_TO_KIND[ext]) return EXT_TO_KIND[ext];
  if (mime === 'application/pdf') return 'pdf';
  if (mime?.includes('word')) return 'word';
  if (mime?.includes('sheet') || mime?.includes('excel')) return 'excel';
  if (mime?.includes('presentation') || mime?.includes('powerpoint')) return 'powerpoint';
  if (mime?.startsWith('text/')) return 'text';
  if (mime?.startsWith('image/')) return 'image';
  return 'other';
}

export function isAllowedDocumentFile(file: File): boolean {
  const ext = getFileExtension(file.name);
  const extOk = DOCUMENT_EXTENSIONS.some((e) => e.slice(1) === ext);
  const mimeOk = file.type ? ALLOWED_DOCUMENT_MIMES.has(file.type) : false;
  return extOk || mimeOk;
}

export function documentKindLabel(kind: DocumentKind): string {
  switch (kind) {
    case 'pdf':
      return 'PDF';
    case 'word':
      return 'DOC';
    case 'excel':
      return 'XLS';
    case 'powerpoint':
      return 'PPT';
    case 'text':
      return 'TXT';
    case 'image':
      return 'IMG';
    default:
      return 'FILE';
  }
}

export function documentKindColor(kind: DocumentKind): string {
  switch (kind) {
    case 'pdf':
      return '#e53935';
    case 'word':
      return '#1e88e5';
    case 'excel':
      return '#43a047';
    case 'powerpoint':
      return '#fb8c00';
    case 'image':
      return '#8e24aa';
    default:
      return '#5f6368';
  }
}
